import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Panel from '../../../common/layout/Panel';
import { getList, create, deleteOne } from '../../../common/v4/BackendProvider';

const ApiTokens = () => {
    const { account_id } = useParams();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTokenName, setNewTokenName] = useState('');
    const [creating, setCreating] = useState(false);
    const [revealedToken, setRevealedToken] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const baseUrl = `/admin/clients/clients/${account_id}/apitokens`;

    const loadTokens = () => {
        setLoading(true);
        getList(baseUrl)
            .then(res => setTokens(res.data.data || []))
            .catch(() => setError('Failed to load tokens.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadTokens();
    }, [account_id]);

    const handleCreate = (e) => {
        e.preventDefault();
        if (!newTokenName.trim()) return;
        setCreating(true);
        setError(null);
        create(baseUrl, { data: { name: newTokenName.trim() } })
            .then(res => {
                setRevealedToken(res.data.data);
                setNewTokenName('');
                loadTokens();
            })
            .catch(() => setError('Failed to create token.'))
            .finally(() => setCreating(false));
    };

    const handleDelete = (tokenId) => {
        if (!confirm('Delete this API token? This cannot be undone.')) return;
        deleteOne(baseUrl, tokenId)
            .then(() => loadTokens())
            .catch(() => setError('Failed to delete token.'));
    };

    const handleCopy = () => {
        if (revealedToken?.token) {
            navigator.clipboard.writeText(revealedToken.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    return (
        <Panel title="API Tokens">
            <div>
            {revealedToken && (
                <div className="alert alert-success alert-dismissible" role="alert">
                    <strong>Token created.</strong> Copy it now — it will not be shown again.
                    <div className="mt-2 d-flex align-items-center gap-2" style={{ gap: '8px' }}>
                        <code className="flex-grow-1" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                            {revealedToken.token}
                        </code>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleCopy}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => { setRevealedToken(null); setCopied(false); }}
                        aria-label="Close"
                    />
                </div>
            )}

            {error && (
                <div className="alert alert-danger alert-dismissible" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
                </div>
            )}

            <form onSubmit={handleCreate} className="mb-4 d-flex" style={{ gap: '8px' }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Token name (e.g. My Integration)"
                    value={newTokenName}
                    onChange={e => setNewTokenName(e.target.value)}
                    disabled={creating}
                    maxLength={80}
                />
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={creating || !newTokenName.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {creating ? 'Creating...' : 'Create Token'}
                </button>
            </form>

            {loading ? (
                <p className="text-muted">Loading...</p>
            ) : tokens.length === 0 ? (
                <p className="text-muted">No API tokens yet.</p>
            ) : (
                <table className="table table-sm table-hover">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tokens.map(token => (
                            <tr key={token.ATRCLIENTAPIACCESSID}>
                                <td>{token.ATRUSERNAME}</td>
                                <td>
                                    <span className={`badge ${token.ATRSTATUS ? 'bg-success' : 'bg-secondary'}`}>
                                        {token.ATRSTATUS ? 'Active' : 'Disabled'}
                                    </span>
                                </td>
                                <td>{formatDate(token.ATRCREATETIME)}</td>
                                <td className="text-end">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(token.ATRCLIENTAPIACCESSID)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            </div>
        </Panel>
    );
};

export default ApiTokens;
